<template>
  <div
    v-if="account"
    v-click-outside="closePanel"
    class="notification-center"
  >
    <button
      type="button"
      class="notification-button"
      :class="{ 'notification-button-active': hasUnread }"
      :aria-expanded="notificationsOpen"
      aria-label="Ver notificaciones"
      @click.stop="togglePanel"
    >
      <font-awesome-icon icon="bell" class="notification-icon" />

      <span v-if="hasUnread" class="notification-badge">
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
    </button>

    <div
      v-show="notificationsOpen"
      class="notification-panel"
      role="dialog"
      aria-label="Notificaciones"
      @keydown.esc="closePanel"
    >
      <div class="notification-panel-header">
        <strong>Notificaciones</strong>
        <button
          v-if="hasUnread"
          type="button"
          class="notification-mark-all"
          @click="markAllRead"
        >
          Marcar todas como leídas
        </button>
      </div>

      <div v-if="loading" class="notification-state">Cargando notificaciones...</div>

      <div v-else-if="error" class="notification-state notification-error">{{ error }}</div>

      <div v-else-if="!notifications.length" class="notification-state">
        No tienes notificaciones vigentes.
      </div>

      <div v-else class="notification-list">
        <template v-for="n in notifications">
          <router-link
            v-if="itemLink(n)"
            :key="n.id"
            :to="itemLink(n)"
            class="notification-item"
            :class="{ 'notification-item--unread': !n.read }"
            @click.native="onItemClick(n)"
          >
            <strong>{{ n.title }}</strong>
            <p>{{ n.message }}</p>
            <small>{{ formatNotificationDate(n.created_at) }}</small>
          </router-link>

          <div
            v-else
            :key="n.id"
            class="notification-item"
            :class="{ 'notification-item--unread': !n.read }"
            role="button"
            tabindex="0"
            @click="onItemClick(n)"
            @keydown.enter="onItemClick(n)"
          >
            <strong>{{ n.title }}</strong>
            <p>{{ n.message }}</p>
            <small>{{ formatNotificationDate(n.created_at) }}</small>
          </div>
        </template>
      </div>

      <div class="notification-panel-footer">
        <router-link to="/notificaciones" class="notification-see-all" @click.native="closePanel">
          Ver todas
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { notificationsApi } from "@/services/notifications/api.js";
import { resolveNotificationLink } from "@/services/notifications/links.js";
import { formatNotificationDate } from "@/services/notifications/format.js";

// El poll golpea solo /me/unread-count (liviano, sin documentos) cada
// POLL_INTERVAL_MS; la bandeja completa se carga solo al abrir el panel.
const POLL_INTERVAL_MS = 45000;

// SENAPRED exige cierre de sesión por inactividad (config.yml, "maxage:
// 21600 ... por solicitud de SENAPRED 06/2026") y auth/middleware.go
// refresca la cookie/JWT en TODA solicitud autenticada — un poll de fondo
// sin este corte mantendría la sesión viva indefinidamente con la pestaña
// solo abierta y abandonada, anulando ese requisito. El poll se detiene
// tanto al ocultar la pestaña (Page Visibility) como tras este umbral sin
// interacción real del usuario, lo que ocurra primero.
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"];

export default {
  name: "NotificationBell",

  props: {
    account: { type: Boolean, default: false },
  },

  data() {
    return {
      notifications: [],
      unreadCount: 0,
      notificationsOpen: false,
      loading: false,
      error: "",
      pollHandle: null,
      lastActivityAt: Date.now(),
    };
  },

  computed: {
    hasUnread() {
      return this.unreadCount > 0;
    },
    terrainPermissions() {
      const permissions = this.$store.getters.getPermissions;
      return (permissions && permissions.terrain) || null;
    },
  },

  watch: {
    account: {
      immediate: true,
      handler(isAuthenticated) {
        if (isAuthenticated) {
          this.refreshUnreadCount();
          this.startPolling();
        } else {
          this.notifications = [];
          this.unreadCount = 0;
          this.notificationsOpen = false;
          this.stopPolling();
        }
      },
    },
  },

  mounted() {
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("focus", this.handleFocus);
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, this.markActivity, { passive: true }));
  },

  beforeDestroy() {
    // Obligatorio: este componente se destruye al navegar a /inicio
    // (App.vue, v-if="!isLoginPage") — sin esto quedarían intervals y
    // listeners huérfanos tras cada logout.
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("focus", this.handleFocus);
    ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, this.markActivity));
  },

  methods: {
    togglePanel() {
      if (this.notificationsOpen) this.closePanel();
      else this.openPanel();
    },
    closePanel() {
      this.notificationsOpen = false;
    },
    async openPanel() {
      this.notificationsOpen = true;
      await this.loadInbox();
    },
    async loadInbox() {
      this.loading = true;
      this.error = "";
      try {
        const data = await notificationsApi.getInbox({ page: 1, limit: 10 });
        this.notifications = data.notifications || [];
        this.unreadCount = data.unread_total || 0;
      } catch (error) {
        console.error(error);
        this.notifications = [];
        this.error = "No fue posible cargar las notificaciones.";
      } finally {
        this.loading = false;
      }
    },

    itemLink(notification) {
      return resolveNotificationLink(notification, this.terrainPermissions);
    },
    // Optimista y sin esperar la red antes de navegar: el click en un ítem
    // con router-link ya dispara la navegación por su cuenta; marcar leído
    // corre en paralelo, no la bloquea.
    async onItemClick(notification) {
      if (notification.read) return;
      const previousUnread = this.unreadCount;
      notification.read = true;
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      try {
        const data = await notificationsApi.markRead(notification.id);
        this.unreadCount = data.unread;
      } catch (error) {
        notification.read = false;
        this.unreadCount = previousUnread;
        this.$store.dispatch("toast/show", { message: "No se pudo marcar la notificación como leída.", type: "error" });
      }
    },
    async markAllRead() {
      const previousReadState = this.notifications.map((n) => n.read);
      const previousUnread = this.unreadCount;
      this.notifications.forEach((n) => {
        n.read = true;
      });
      this.unreadCount = 0;
      try {
        const data = await notificationsApi.markAllRead();
        this.unreadCount = data.unread;
      } catch (error) {
        this.notifications.forEach((n, index) => {
          n.read = previousReadState[index];
        });
        this.unreadCount = previousUnread;
        this.$store.dispatch("toast/show", { message: "No se pudieron marcar las notificaciones como leídas.", type: "error" });
      }
    },

    formatNotificationDate,

    async refreshUnreadCount() {
      try {
        const data = await notificationsApi.getUnreadCount();
        this.unreadCount = data.unread;
      } catch (error) {
        // 401/403/423 en background: la sesión ya se cayó. Sin toast, sin
        // reload — solo dejar de pollear; la próxima acción real del
        // usuario mostrará la sesión caída por el camino normal (ver
        // errorHandler en main.js, que este cliente deliberadamente no
        // hereda). Error de red: no-op, se reintenta en el siguiente tick.
        const status = error.response && error.response.status;
        if (status === 401 || status === 403 || status === 423) this.stopPolling();
      }
    },

    isIdle() {
      return Date.now() - this.lastActivityAt > IDLE_THRESHOLD_MS;
    },
    markActivity() {
      const wasIdle = this.isIdle();
      this.lastActivityAt = Date.now();
      if (wasIdle && this.account && document.visibilityState === "visible") this.startPolling();
    },
    handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        this.markActivity();
        if (this.account) {
          this.refreshUnreadCount();
          this.startPolling();
        }
      } else {
        this.stopPolling();
      }
    },
    handleFocus() {
      this.markActivity();
      if (this.account && document.visibilityState === "visible") {
        this.refreshUnreadCount();
        this.startPolling();
      }
    },
    startPolling() {
      if (this.pollHandle || document.visibilityState !== "visible") return;
      this.pollHandle = setInterval(() => {
        if (this.isIdle()) {
          this.stopPolling();
          return;
        }
        this.refreshUnreadCount();
      }, POLL_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollHandle) {
        clearInterval(this.pollHandle);
        this.pollHandle = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notification-center {
  position: relative;

  display: flex;
  align-items: center;

  height: 100%;
}

.notification-button {
  position: relative;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  width: 38px;
  height: 38px;
  padding: 0;

  border: 1px solid transparent;
  border-radius: 50%;

  background: transparent;
  color: var(--color-white);

  font-family: inherit;

  cursor: pointer;

  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    background-color 0.2s ease;
}

.notification-button:hover,
.notification-button-active {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-orange);
}

.notification-button:focus {
  outline: 2px solid var(--color-orange);
  outline-offset: 2px;
}

.notification-icon {
  font-size: 1.05rem;
}

/* Contador de notificaciones */

.notification-badge {
  position: absolute;
  top: -2px;
  right: -2px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  min-width: 18px;
  height: 18px;
  padding: 0 4px;

  border: 2px solid var(--color-navy);
  border-radius: 999px;

  background: var(--color-orange);
  color: var(--color-white);

  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
}

/* Panel desplegable */

.notification-panel {
  position: absolute;
  top: calc(100% - 4px);
  left: 0;
  z-index: 1200;

  width: 360px;
  max-width: calc(100vw - 2em);

  overflow: hidden;

  border: 1px solid var(--color-fog);
  border-radius: 6px;

  background: var(--color-white);

  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.24);
}

/* Encabezado del panel */

.notification-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1em;

  padding: 0.85em 1em;

  border-bottom: 1px solid var(--color-fog);

  background: var(--color-mist);
  color: var(--color-navy);
}

.notification-panel-header strong {
  font-size: 0.86rem;
}

.notification-mark-all {
  border: 0;
  background: transparent;

  color: var(--color-steel);

  font-size: 0.7rem;
  font-weight: 600;
  text-decoration: underline;

  cursor: pointer;
}

.notification-mark-all:hover {
  color: var(--color-orange);
}

/* Lista de notificaciones */

.notification-list {
  max-height: 360px;

  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.notification-item {
  display: block;

  padding: 0.9em 1em;
  padding-left: calc(1em - 3px);
  border-left: 3px solid transparent;

  border-bottom: 1px solid var(--color-fog);

  background: var(--color-white);

  text-align: left;
  text-decoration: none;
  cursor: pointer;

  transition: background-color 0.2s ease;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background: var(--color-mist);
}

.notification-item--unread {
  border-left-color: var(--color-orange);
  background: var(--color-mist);
}

.notification-item strong {
  display: block;

  color: var(--color-navy);

  font-size: 0.82rem;
  line-height: 1.35;
}

.notification-item--unread strong {
  font-weight: 800;
}

.notification-item p {
  margin: 0.35em 0;

  color: var(--color-graphite);

  font-size: 0.76rem;
  line-height: 1.45;

  overflow-wrap: anywhere;
}

.notification-item small {
  display: block;

  color: var(--color-ghost);

  font-size: 0.68rem;
}

/* Estados del panel */

.notification-state {
  padding: 1.3em 1em;

  color: var(--color-ghost);

  font-size: 0.78rem;
  text-align: center;
}

.notification-error {
  color: var(--color-danger);
}

/* Pie del panel */

.notification-panel-footer {
  padding: 0.7em 1em;

  border-top: 1px solid var(--color-fog);

  background: var(--color-mist);

  text-align: center;
}

.notification-see-all {
  color: var(--color-steel);

  font-size: 0.76rem;
  font-weight: 600;
  text-decoration: none;
}

.notification-see-all:hover {
  color: var(--color-orange);
  text-decoration: underline;
}

@media only screen and (max-width: 640px) {
  .notification-button {
    width: 35px;
    height: 35px;
  }

  .notification-panel {
    position: fixed;
    top: 4.5em;
    right: 1em;
    left: 1em;

    width: auto;
    max-width: none;
  }

  .notification-list {
    max-height: 55vh;
  }
}
</style>
